import React, { useMemo, ReactElement } from 'react';
import Table from '../../../components/table/table.component';
import TableHead from '../../../components/table-head/table-head.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import Tooltip from '../../../components/tooltip/tooltip.component';
import ActionButton from '../../../components/action-button/action-button.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import FormFieldSelect from '../../../components/form-field-select/form-field-select.component';
import FormFieldNumber from '../../../components/form-field-number/form-field-number.component';
import TableRowAddNewItem from '../../../components/table-row-add-new-item/table-row-add-new-item.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import SinglePageTab from '../../app/common/single-page-tab/single-page-tab.component';
import RemoveIcon from '../../../icons/remove-icon';
import FormService from '../../../services/form-service';

const AssetAllocation: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const COLUMNS_TOTAL: number = 2;
  const PORTFOLIO_HIDDEN_LABEL: string = 'Hidden';

  const {
    isSaving,
    isSaveDisabled,
    isEditMode,
    isRightDrawerOpen,
    isMultiplePortfolioGroupView,
    formError,
    assetAllocationItems,
    assetAllocationNewItemIds,
    portfolioList,
    hiddenPortfolioList,
    onCancelForm,
    onSubmitForm,
    onAddNewAssetAllocationItem,
    onAssetAllocationPortfolioChange,
    onDeleteAssetAllocationItemClick,
  } = props;

  const isEmpty: boolean = !isEditMode && (!assetAllocationItems || !Object.keys(assetAllocationItems).length);
  const formPrefix: string = 'assetData';

  const commonPortfolioList: Portfolio[] = useMemo((): Portfolio[] => [
    ...(portfolioList || []),
    ...(hiddenPortfolioList ? hiddenPortfolioList.map((portfolioItem: Portfolio): Portfolio => ({
      ...portfolioItem,
      name: `${portfolioItem.name} (${PORTFOLIO_HIDDEN_LABEL})`,
    })) : []),
  ], [portfolioList, hiddenPortfolioList]);

  const menuItemPortfolioList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    isGrouped: isMultiplePortfolioGroupView,
    groupKey: 'group',
    groupItemLabelKey: 'name',
    optionItemLabelKey: 'name',
    optionList: commonPortfolioList,
  }), [commonPortfolioList, isMultiplePortfolioGroupView]);

  return (
    <SinglePageTab
      withNoPadding
      isSaving={isSaving}
      isEmpty={isEmpty}
      isSaveDisabled={isSaveDisabled}
      isEditMode={isEditMode}
      isRightDrawerOpen={isRightDrawerOpen}
      formError={formError}
      onSubmitForm={onSubmitForm}
      onCancelForm={onCancelForm}
    >
      {
        (!isEmpty || isEditMode) && (
          <Table>
            <TableHead isSubHeader>
              <TableRow>
                <TableCell>
                  Portfolio
                </TableCell>
                <TableCell>
                  Allocation, %
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody isPlain withZebraRows withTwoColumns withHeaderColumn>
              {
                ((assetAllocationItems && Object.values(assetAllocationItems)) || []).map((
                  assetAllocationItem: AssetAllocationItem,
                ): ReactElement => {
                  const isNewItem: boolean = assetAllocationNewItemIds && assetAllocationNewItemIds.length
                    ? assetAllocationNewItemIds.includes(assetAllocationItem.id) : false;
                  const formKeyAssetAllocationItem: string = FormService.getIndexedFormFieldKey(
                    assetAllocationItem.id,
                  );

                  return (
                    <TableRow isActive={isNewItem} key={assetAllocationItem.id}>
                      <TableCell>
                        {
                          isEditMode ? (
                            <FormFieldSelect
                              isBordered
                              withBlankElement
                              isDisabled={portfolioList && !portfolioList.length}
                              name={`${formPrefix}.allocationItems[${formKeyAssetAllocationItem}].portfolioId`}
                              optionsTotal={portfolioList && portfolioList.length}
                              onChange={((portfolioId: number): void => {
                                if (onAssetAllocationPortfolioChange) {
                                  onAssetAllocationPortfolioChange(portfolioId, assetAllocationItem.id);
                                }
                              })}
                            >
                              {menuItemPortfolioList}
                            </FormFieldSelect>
                          ) : (
                            (assetAllocationItem.portfolio && (
                              `${isMultiplePortfolioGroupView && assetAllocationItem.portfolio.group
                                ? `${assetAllocationItem.portfolio.group.name}: ` : ''}`
                              + `${assetAllocationItem.portfolio.name}`
                            )) || ''
                          )
                        }
                      </TableCell>
                      <TableCell>
                        {
                          isEditMode ? (
                            <FlexContainer>
                              <FormFieldNumber
                                isFloat
                                isPercent
                                isBordered
                                name={`${formPrefix}.allocationItems[${formKeyAssetAllocationItem}].value`}
                              />
                              <Tooltip title="Delete">
                                <ActionButton
                                  isInline
                                  withOffset
                                  withNoPadding
                                  onClick={(): void => {
                                    onDeleteAssetAllocationItemClick(assetAllocationItem.id);
                                  }}
                                >
                                  <RemoveIcon />
                                </ActionButton>
                              </Tooltip>
                            </FlexContainer>
                          ) : assetAllocationItem.value
                        }
                      </TableCell>
                    </TableRow>
                  )
                })
              }
              {
                isEditMode && (
                  <TableRowAddNewItem colSpan={COLUMNS_TOTAL} onClick={onAddNewAssetAllocationItem} />
                )
              }
            </TableBody>
          </Table>
        )
      }
    </SinglePageTab>
  );
});

export default AssetAllocation;
